/**
 * StatCard — KPI / metric tile.
 * Used across Dashboard, Close Shift, Fund Management.
 *
 * Usage:
 *   <StatCard label="Today's Sales" value="₱48,250" delta="+12.4%" deltaTone="positive"
 *             footer="vs yesterday ₱42,930" icon={ShoppingCart} iconTone="success" />
 */

import { ArrowUp, ArrowDown } from 'lucide-react';

const ICON_TONES = {
  success: { bg: '#DCFCE7', color: '#16A34A' },
  warning: { bg: '#FEF3C7', color: '#D97706' },
  danger:  { bg: '#FEE2E2', color: '#DC2626' },
  info:    { bg: '#DBEAFE', color: '#2563EB' },
  neutral: { bg: '#F1F5F9', color: '#64748B' },
  brand:   { bg: '#DCFCE7', color: '#1A4D2E' },
};

export function StatCard({
  label,
  value,
  delta,
  deltaTone = 'positive',
  footer,
  icon: Icon,
  iconTone = 'neutral',
  className = '',
}) {
  const tone = ICON_TONES[iconTone] || ICON_TONES.neutral;
  const isPositiveDelta = deltaTone === 'positive';
  const DeltaArrow = isPositiveDelta ? ArrowUp : ArrowDown;

  return (
    <div
      className={`bg-[var(--surface-card)] rounded-lg p-4 flex-1 min-w-0 ${className}`}
      style={{ border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-card)' }}
    >
      <div className="flex items-start justify-between mb-3">
        <span
          className="text-[11px] font-semibold uppercase tracking-wide"
          style={{ color: 'var(--text-secondary)' }}
        >
          {label}
        </span>
        {Icon && (
          <span
            className="inline-flex items-center justify-center w-7 h-7 rounded-md flex-shrink-0"
            style={{ backgroundColor: tone.bg }}
          >
            <Icon size={14} style={{ color: tone.color }} />
          </span>
        )}
      </div>

      <div
        className="text-[28px] font-semibold leading-none"
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontFeatureSettings: '"tnum"',
          fontFamily: 'Manrope, sans-serif',
          color: 'var(--text-primary)',
        }}
      >
        {value}
      </div>

      {(delta || footer) && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {delta && (
            <span
              className="inline-flex items-center gap-0.5 h-5 px-1.5 rounded text-[10px] font-semibold"
              style={{
                backgroundColor: isPositiveDelta ? '#DCFCE7' : '#FEE2E2',
                color: isPositiveDelta ? '#166534' : '#991B1B',
              }}
            >
              <DeltaArrow size={9} />
              {delta}
            </span>
          )}
          {footer && (
            <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
              {footer}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
